import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import AppBar from "@/components/layout/AppBar";
import { useActiveFarmContext, useApiContext } from "@/contexts";
import PageLoading from "./pages/loading";
import Router from "@/router/routes";

const App = () => {
  const { loginWithRedirect, logout, isAuthenticated, isLoading, user } =
    useAuth0();
  const [farms, setFarms] = useState([]);
  const [farmsError, setFarmsError] = useState(null);
  const { activeFarm, setActiveFarm } = useActiveFarmContext();
  const { api, loading: apiLoading, error: apiError } = useApiContext();

  const handleActiveFarmChange = (farm) => {
    setActiveFarm(farm);
  };

  // Sync user data on login
  useEffect(() => {
    if (isAuthenticated && user && api) {
      api.syncUser(user.email, user.name).catch((err) => {
        console.error("Failed to sync user:", err);
      });
    }
  }, [isAuthenticated, user, api]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const loadFarms = async () => {
      setFarmsError(null);

      try {
        const myFarms = await api.getFarms();
        setFarms(myFarms);
        // TODO: setActiveFarm to newly created?
      } catch (err) {
        console.error("Failed to load farms:", err);
        setFarmsError(err.message || "Failed to load farms");
        setFarms([]);
      }
    };

    loadFarms();

    const handlePostFarm = () => {
      loadFarms();
      // TODO: select newly created farm after creation
    };

    api.addEventListener("postFarm", handlePostFarm);

    return () => {
      api.removeEventListener("postFarm", handlePostFarm);
    };
  }, [api]);

  if (isLoading || apiLoading) {
    return <PageLoading loadingText="Initializing application..." />;
  }

  return (
    <Box className="app-box" sx={{ width: "100%" }}>
      <AppBar
        loginWithRedirect={loginWithRedirect}
        logout={logout}
        isAuthenticated={isAuthenticated}
        farms={farms}
        activeFarm={activeFarm}
        onSelectActiveFarm={handleActiveFarmChange}
        user={user}
      >
        {farmsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">Unable to load farms</Typography>
          </Alert>
        )}
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">API Connection Error</Typography>
          </Alert>
        )}
        <Box sx={{ width: "100%", display: "flex" }}>
          <Router />
        </Box>
      </AppBar>
    </Box>
  );
};

export default App;
